import { useCallback, useMemo } from 'react';
import { useEventFeatureFlags } from '@shared/hooks/useEventFeatureFlags/useEventFeatureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { MigratedCustomPageSlug } from '@shared/constants/customPageMigration';
import { MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG } from '@shared/constants/customPageMigration';

export type MigratedCustomPageEventFeatureFlag = (typeof MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG)[MigratedCustomPageSlug];

type UseMigratedCustomPageFeatureFlagsReturn = Readonly<{
  isLoading: boolean;
  hasData: boolean;
  enabledSlugs: ReadonlySet<MigratedCustomPageSlug>;
  isMigratedCustomPageEnabled: (slug: string) => boolean;
}>;

export const useMigratedCustomPageFeatureFlags = (): UseMigratedCustomPageFeatureFlagsReturn => {
  const { eventHandle } = useEventInfo();
  const { loadingEventFeatureFlags, hasEventFeatureFlagData, isEventFeatureFlagEnabled } = useEventFeatureFlags({
    eventHandle: eventHandle || '',
    skip: !eventHandle,
  });

  const enabledSlugs = useMemo(() => {
    if (!eventHandle || loadingEventFeatureFlags) return new Set<MigratedCustomPageSlug>();

    const enabled = new Set<MigratedCustomPageSlug>();
    (Object.keys(MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG) as ReadonlyArray<MigratedCustomPageSlug>).forEach(slug => {
      const flag = MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG[slug];
      if (isEventFeatureFlagEnabled(flag)) {
        enabled.add(slug);
      }
    });

    return enabled;
  }, [eventHandle, loadingEventFeatureFlags, isEventFeatureFlagEnabled]);

  const isMigratedCustomPageEnabled = useCallback(
    (slug: string) => {
      if (!slug || loadingEventFeatureFlags) return false;
      return enabledSlugs.has(slug as MigratedCustomPageSlug);
    },
    [enabledSlugs, loadingEventFeatureFlags],
  );

  return {
    isLoading: loadingEventFeatureFlags,
    hasData: hasEventFeatureFlagData,
    enabledSlugs,
    isMigratedCustomPageEnabled,
  };
};
